/**
 * /dev/calendar-export-playground
 *
 * Dev-only page for exercising the DeadlineCalendarExportModal end-to-end
 * against a real deployed backend. PR-3 of Watchtower Calendar Export; per
 * TASK-015 acceptance criteria, the modal renders standalone here with no
 * production-surface changes (TASK-016 wires the trigger into Watchtower).
 *
 * Inputs:
 *   - caseFileId  (required) — also accepted as ?caseFileId= query param
 *   - deadlineId  (required) — also accepted as ?deadlineId=  query param
 *
 * Workflow:
 *   1. User pastes/preloads IDs and clicks "Load deadline".
 *   2. Page calls the existing `getDeadline` callable to validate access and
 *      pull the real Deadline + recipientName (CaseFile.name).
 *   3. User clicks "Open export modal" to launch the export flow.
 *   4. Modal owns the strict-sequential audit → generate → download
 *      pipeline against the real `logDeadlineCalendarExport` Cloud Function.
 *
 * The page asks for IDs rather than enumerating CaseFiles to keep the
 * playground intentionally minimal — it is not a feature, it is a test
 * driver for verification.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { Loader2 } from 'lucide-react';
import { db, functions } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useDeadlineCalendarExport } from '../hooks/useDeadlineCalendarExport';
import type { Deadline } from '../components/deadlines/types';
import { formatDate, formatDaysRemaining } from '../components/deadlines/types';

interface GetDeadlineResponse {
  success: boolean;
  deadline?: Deadline;
  error?: string;
}

export function CalendarExportPlayground(): JSX.Element {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [caseFileIdInput, setCaseFileIdInput] = useState(params.get('caseFileId') ?? '');
  const [deadlineIdInput, setDeadlineIdInput] = useState(params.get('deadlineId') ?? '');
  const [deadline, setDeadline] = useState<Deadline | null>(null);
  const [recipientName, setRecipientName] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastExportResult, setLastExportResult] = useState<string | null>(null);

  const { open, Modal } = useDeadlineCalendarExport({
    onExported: (result) => {
      setLastExportResult(
        `Modal reported export complete (auditLogged=${result.auditLogged}). Check audit_logs collection for the new entry.`,
      );
    },
  });

  // Auto-load on first render if both query params were supplied.
  useEffect(() => {
    if (params.get('caseFileId') && params.get('deadlineId') && !deadline && !loading) {
      void handleLoad(params.get('caseFileId')!, params.get('deadlineId')!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLoad(caseFileId: string, deadlineId: string): Promise<void> {
    setError(null);
    setDeadline(null);
    setRecipientName('');
    setLastExportResult(null);
    setLoading(true);
    try {
      // Pull the deadline via the existing callable (handles ACL).
      const callable = httpsCallable<
        { caseFileId: string; deadlineId: string },
        GetDeadlineResponse
      >(functions, 'getDeadline');
      const result = await callable({ caseFileId, deadlineId });
      if (!result.data.success || !result.data.deadline) {
        throw new Error(result.data.error || 'Failed to load deadline');
      }
      const loaded = result.data.deadline;
      setDeadline(loaded);

      // Pull CaseFile.name for the recipient toggle. CaseFile lives at
      // users/{ownerId}/caseFiles/{caseFileId}; we fetch via the user's own
      // collection first (most common), then fall back to a collection-group
      // query if needed. For this playground we only support the
      // own-CaseFile case — that's sufficient for verification, and care-
      // team-membership-based access already passes the getDeadline ACL above.
      if (user?.uid) {
        try {
          const cfRef = doc(db, 'users', user.uid, 'caseFiles', caseFileId);
          const snap = await getDoc(cfRef);
          if (snap.exists()) {
            const name = (snap.data() as { name?: string }).name;
            if (typeof name === 'string') setRecipientName(name);
          }
        } catch (cfErr) {
          // Non-fatal; the modal will run without the recipient name and the
          // PHI toggle will simply not have a name to include. Logged for
          // diagnostics.
           
          console.warn('[playground] could not fetch CaseFile.name:', cfErr);
        }
      }

      // Reflect IDs in the URL so the test URL is shareable.
      setParams({ caseFileId, deadlineId }, { replace: true });
    } catch (err) {
       
      console.error('[playground] load failure:', err);
      setError(err instanceof Error ? err.message : 'Failed to load deadline');
    } finally {
      setLoading(false);
    }
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (caseFileIdInput.trim() && deadlineIdInput.trim()) {
      void handleLoad(caseFileIdInput.trim(), deadlineIdInput.trim());
    }
  }

  function handleOpenModal(): void {
    if (deadline) {
      open(deadline, { recipientName });
    }
  }

  return (
    <div className="min-h-screen bg-airy p-6">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="card">
          <div className="card-header">
            <h1 className="text-lg font-semibold text-ink">
              Calendar Export — Dev Playground
            </h1>
            <p className="text-sm text-muted mt-1">
              Internal test driver for the export modal (TASK-015). Not part
              of the production user surface.
            </p>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label
                  htmlFor="playground-casefile-id"
                  className="block text-sm font-medium text-ink mb-1"
                >
                  CaseFile ID
                </label>
                <input
                  id="playground-casefile-id"
                  type="text"
                  value={caseFileIdInput}
                  onChange={(e) => setCaseFileIdInput(e.target.value)}
                  className="w-full rounded-md border border-soft-neutral px-3 py-2 text-sm font-mono"
                  placeholder="e.g. abc123…"
                  spellCheck={false}
                />
              </div>
              <div>
                <label
                  htmlFor="playground-deadline-id"
                  className="block text-sm font-medium text-ink mb-1"
                >
                  Deadline ID
                </label>
                <input
                  id="playground-deadline-id"
                  type="text"
                  value={deadlineIdInput}
                  onChange={(e) => setDeadlineIdInput(e.target.value)}
                  className="w-full rounded-md border border-soft-neutral px-3 py-2 text-sm font-mono"
                  placeholder="e.g. dl_…"
                  spellCheck={false}
                />
              </div>
              <button
                type="submit"
                disabled={loading || !caseFileIdInput.trim() || !deadlineIdInput.trim()}
                className="btn-primary flex items-center gap-2 disabled:opacity-50"
              >
                {loading && <Loader2 size={14} className="animate-spin" />}
                Load deadline
              </button>
            </form>
          </div>
        </div>

        {error && (
          <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </div>
        )}

        {deadline && (
          <div className="card">
            <div className="card-header">
              <h2 className="text-base font-semibold text-ink">Loaded deadline</h2>
            </div>
            <div className="card-body space-y-2 text-sm">
              <div>
                <span className="text-muted">Title:</span>{' '}
                <span className="text-ink">{deadline.title}</span>
              </div>
              <div>
                <span className="text-muted">Due:</span>{' '}
                <span className="text-ink">
                  {formatDate(deadline.dueDate)} ({formatDaysRemaining(deadline.dueDate)})
                </span>
              </div>
              <div>
                <span className="text-muted">Type / Priority:</span>{' '}
                <span className="text-ink">
                  {deadline.type} / {deadline.priority}
                </span>
              </div>
              <div>
                <span className="text-muted">CaseFile name (recipientName for toggle):</span>{' '}
                <span className="text-ink">
                  {recipientName || <em className="text-muted">(not loaded — toggle will have nothing to include)</em>}
                </span>
              </div>
              <div className="pt-3">
                <button
                  type="button"
                  onClick={handleOpenModal}
                  className="btn-primary"
                >
                  Open export modal
                </button>
              </div>
            </div>
          </div>
        )}

        {lastExportResult && (
          <div className="rounded-md border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-800">
            {lastExportResult}
          </div>
        )}
      </div>

      <Modal />
    </div>
  );
}

export default CalendarExportPlayground;
